import { ThunkDispatch } from "core-integration/src/store";
import { getElementsById } from "core-integration/src/store/elements/reducers";
import {
  showElementSummary,
  updateElementTypeControl,
} from "core-integration/src/store/elements/thunks";
import { IElement } from "core-integration/src/store/elements/types";
import { getNextEvent } from "core-integration/src/store/events/reducers";
import { getRules } from "core-integration/src/store/game/reducers";
import { IPick } from "core-integration/src/store/my-team/types";
import { resetLastChange } from "core-integration/src/store/squad/actions";
import {
  getErrors,
  getLastChange,
  getProposedElements,
  getSavedPicks,
  getSquadMode,
} from "core-integration/src/store/squad/reducers";
import {
  removeElement,
  restoreElement,
} from "core-integration/src/store/squad/thunks";
import { getTeamsById } from "core-integration/src/store/teams/reducers";
import {
  formatRawAsISO,
  formatRawAsLocalI18n,
} from "core-integration/src/utils/datetime";
import { integerToMoney } from "core-integration/src/utils/money";
import * as React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Trans, WithTranslation, withTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useBlocker } from "react-router";
import styled from "styled-components";
import { dateLocales } from "../../i18n";
import Alert, { AlertWrap } from "../Alert";
import Box from "../Box";
import Button from "../Button";
import DeadlineBar from "../DeadlineBar";
import Dialog, { DialogButtonItem } from "../Dialog";
import DialogHeading from "../DialogHeading";
import DialogManager from "../DialogManager";
import PageAd from "../PageAd";
import Pitch, { PitchRow, PitchUnit } from "../Pitch";
import TabPanel from "../tabs/TabPanel";
import Tabs from "../tabs/Tabs";
import UnsavedChangesDialog from "../UnsavedChangesDialog";
import SaveBar from "./SaveBar";
import Scoreboard from "./Scoreboard";
import SquadPitchUnit from "./SquadPitchUnit";

const StyledPitchWrapper = styled.div`
  margin-top: ${({ theme }) => theme.space[5]};
  padding-top: ${({ theme }) => theme.space[4]};
  background-color: ${({ theme }) => theme.colors.secondaryTwo};

  @media (min-width: ${({ theme }) => theme.breakpoints[3]}) {
    border-radius: ${({ theme }) => theme.radii[2]};
  }
`;

const PlayerActions = styled.div`
  @media (min-width: ${({ theme }) => theme.breakpoints[3]}) {
    margin: 0 auto;
  }
`;

const ScoreBoardWrapper = styled.div`
  margin: ${({ theme }) => `${theme.space[4]} 0`};
  padding: ${({ theme }) => theme.space[2]} 0;
  background-color: ${({ theme }) => theme.colors.secondary};

  @media (min-width: ${({ theme }) => theme.breakpoints[3]}) {
    border-radius: ${({ theme }) => theme.radii[1]};
    padding: ${({ theme }) => theme.space[2]};
  }
`;

//TODO: Move IListTableProps somewhere more abstract?
export interface IListTableProps {
  elementType: number;
  key: string;
  positions: number[];
  renderElementDialog: (element: IElement) => void;
  renderElementMenu: (position: number) => void;
}

interface IOwnProps {
  listTable: (listTableProps: IListTableProps) => React.ReactNode;
  scoreboard: string;
  showSidebar: () => void;
  submitDialog: (
    handleHide: () => void,
    allowNavigation: () => void,
  ) => React.ReactNode;
}

type Props = IOwnProps & WithTranslation;

const CreateSquad: React.FC<Props> = ({
  i18n,
  listTable,
  scoreboard,
  showSidebar,
  submitDialog,
  t,
}) => {
  const dispatch = useDispatch<ThunkDispatch>();

  const currencyDivisor = 10;
  const elementsById = useSelector(getElementsById);
  const errors = useSelector(getErrors);
  const lastChange = useSelector(getLastChange);
  const mode = useSelector(getSquadMode);
  const nextEvent = useSelector(getNextEvent);
  const proposedElements = useSelector(getProposedElements);
  const savedPicks = useSelector(getSavedPicks);
  const rules = useSelector(getRules);
  const teamsById = useSelector(getTeamsById);

  const [positionForMenu, setPositionForMenu] = useState<number>(0);

  const hasUnsavedChanges = useMemo(() => {
    const positions = new Set();
    Object.keys(savedPicks).forEach((key) => positions.add(key));
    Object.keys(proposedElements).forEach((key) => positions.add(key));

    for (const key of positions) {
      const pos = Number(key);
      const proposed = proposedElements[pos];
      const saved = savedPicks[pos];

      // Check if player added/removed at position
      if (proposed?.id !== saved?.element) {
        return true;
      }
    }

    return false;
  }, [proposedElements, savedPicks]);

  const skipBlockRef = useRef(false);

  // Callback to allow navigation
  // e.g. when confirming transfers in ConfirmTransfersDialog
  const allowNavigation = () => {
    skipBlockRef.current = true;
  };
  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (skipBlockRef.current) {
      return false;
    }

    return (
      hasUnsavedChanges && currentLocation.pathname !== nextLocation.pathname
    );
  });

  const handleShowMenuForElement = (position: number) =>
    setPositionForMenu(position);

  const handleHideMenuForElement = () => setPositionForMenu(0);

  const handleRemoveElement = (position: number) => {
    handleHideMenuForElement();
    dispatch(removeElement(position));
  };

  const handleRestoreElement = (position: number) => {
    handleHideMenuForElement();
    dispatch(restoreElement(position));
  };

  const showElementType = (elementTypeId: number) => {
    handleHideMenuForElement();
    dispatch(updateElementTypeControl(elementTypeId));
    showSidebar();
  };

  const showDialog = (element: IElement) => {
    dispatch(showElementSummary(element.id));
    handleHideMenuForElement();
  };

  const renderMenu = () => {
    let element: IElement | undefined;
    let mode: "remove" | "restore" | undefined;
    if (proposedElements[positionForMenu]) {
      element = proposedElements[positionForMenu];
      mode = "remove";
    } else if (savedPicks[positionForMenu]) {
      element = elementsById[savedPicks[positionForMenu].element];
      mode = "restore";
    }
    if (!mode || !element) {
      return null;
    }
    return (
      <Dialog closeDialog={handleHideMenuForElement}>
        <Dialog.Header closeDialog={handleHideMenuForElement}>
          <DialogHeading>
            {`${element.first_name} ${element.second_name}`}
          </DialogHeading>
        </Dialog.Header>
        <Dialog.Body>
          <PlayerActions>
            {mode === "remove" ? (
              <DialogButtonItem>
                <Button
                  variant="primary"
                  onClick={() => handleRemoveElement(positionForMenu)}
                  width={1}
                >
                  {t("createSquad.removePlayer", "Remove player")}
                </Button>
              </DialogButtonItem>
            ) : mode === "restore" ? (
              <>
                <DialogButtonItem>
                  <Button
                    variant="primary"
                    onClick={() => handleRestoreElement(positionForMenu)}
                    width={1}
                  >
                    {t("createSquad.restorePlayer", "Restore player")}
                  </Button>
                </DialogButtonItem>
                <DialogButtonItem>
                  <Button
                    variant="primary"
                    onClick={() => showElementType(element!.element_type)}
                    width={1}
                  >
                    {t("createSquad.replacePlayer", "Select replacement")}
                  </Button>
                </DialogButtonItem>
              </>
            ) : (
              ""
            )}
            <DialogButtonItem>
              <Button
                variant="primary"
                onClick={() => showDialog(element as IElement)}
                width={1}
              >
                {t("createSquad.viewInformation", "View Information")}
              </Button>
            </DialogButtonItem>
          </PlayerActions>
        </Dialog.Body>
      </Dialog>
    );
  };

  const renderElementValueForPosition = (pos: number) => {
    const element: IElement = proposedElements[pos];
    const pick: IPick | undefined = savedPicks[pos];
    // Initial element selection (squad selection)
    if (!pick && element) {
      return integerToMoney(element.now_cost, currencyDivisor);
    }
    // Removed element (transfers)
    if (pick && !element) {
      return integerToMoney(pick.selling_price, currencyDivisor);
    }
    // Original element (transfers)
    if (pick && element && pick.element === element.id) {
      return integerToMoney(pick.selling_price, currencyDivisor);
    }
    // Replaced element (transfers)
    if (pick && element && pick.element !== element.id) {
      return integerToMoney(element.now_cost, currencyDivisor);
    }
    return null;
  };

  useEffect(() => {
    dispatch(resetLastChange());
  }, [dispatch]);

  let latestAction: React.ReactNode = null;
  if (lastChange.type === "addition") {
    latestAction = (
      <Alert type="success">
        <Trans
          i18nKey="createSquad.alert.elementAdded"
          values={{ playerName: elementsById[lastChange.element].web_name }}
        >
          <strong>{elementsById[lastChange.element].web_name}</strong> has been
          added to your squad.
        </Trans>
      </Alert>
    );
  } else if (lastChange.type === "removal") {
    latestAction = (
      <Alert type="success">
        <Trans
          i18nKey="createSquad.alert.elementRemoved"
          values={{ playerName: elementsById[lastChange.element].web_name }}
        >
          <strong>{elementsById[lastChange.element].web_name}</strong> has been
          removed from your squad.
        </Trans>
      </Alert>
    );
  }

  // Button enablers
  const canEnter = !Object.keys(errors).length;

  // Helper functions to generate props used by multiple component instances
  const sharedPitchElementProps = (pos: number) => ({
    pos,
    renderElementMenu: handleShowMenuForElement,
    elementValue: renderElementValueForPosition(pos),
    showSidebar,
  });

  const sharedSquadListTableProps = (
    pos: number[],
    elementType: number,
    key: string,
  ) => ({
    elementType,
    key,
    positions: pos,
    renderElementDialog: showDialog,
    renderElementMenu: handleShowMenuForElement,
  });

  const submitButtonText = {
    selection: t("createSquad.enterSquad", "Enter Squad"),
    transfers: t("createSquad.makeTransfers", "Make Transfers"),
  };

  const positionsByElementType: {
    [index: string]: number[];
  } = {
    1: [1, 2],
    2: [3, 4, 5, 6, 7],
    3: [8, 9, 10, 11, 12],
    4: [13, 14, 15],
  };

  return (
    <div>
      {blocker.state === "blocked" && (
        <UnsavedChangesDialog
          handleConfirm={() => blocker.state === "blocked" && blocker.proceed()}
          handleHide={() => blocker.state === "blocked" && blocker.reset()}
        />
      )}
      {rules && (
        <>
          <PageAd />
          <Box mx={2} mb={4}>
            <p>
              {t(
                "createSquad.help",
                "Select a maximum of {{ teamLimit }}  players from a single team or 'Auto Pick' if you're short of time.",
                { teamLimit: rules.squad_team_limit },
              )}
            </p>
          </Box>
        </>
      )}
      <ScoreBoardWrapper>
        {nextEvent && (
          <DeadlineBar
            deadlineISO={formatRawAsISO(nextEvent.deadline_time)}
            deadlineLocal={formatRawAsLocalI18n(
              nextEvent.deadline_time,
              dateLocales[i18n.language],
            )}
            headingText={t(
              "createSquad.eventDeadline",
              "{{ eventName }} deadline",
              {
                eventName: nextEvent.name,
              },
            )}
          />
        )}
        <Scoreboard scoreboard={scoreboard} />
      </ScoreBoardWrapper>
      {errors.overTeamLimit && (
        <AlertWrap>
          <Alert type="error">
            {t(
              "createSquad.errors.overTeamLimit",
              "Too many players selected from",
            )}{" "}
            <strong>
              {errors.overTeamLimit
                .map((team) => teamsById[team].name)
                .join(", ")}
            </strong>
          </Alert>
        </AlertWrap>
      )}
      {latestAction && <AlertWrap>{latestAction}</AlertWrap>}

      <StyledPitchWrapper>
        <Tabs centered>
          <TabPanel
            label={t("createSquad.pitchView", "Pitch View")}
            link="pitch"
          >
            <Box mx={-2} pt={4}>
              <Pitch>
                <PitchRow>
                  <PitchUnit />
                  <PitchUnit>
                    <SquadPitchUnit {...sharedPitchElementProps(1)} />
                  </PitchUnit>
                  <PitchUnit>
                    <SquadPitchUnit {...sharedPitchElementProps(2)} />
                  </PitchUnit>
                  <PitchUnit />
                </PitchRow>
                <PitchRow>
                  <PitchUnit>
                    <SquadPitchUnit {...sharedPitchElementProps(3)} />
                  </PitchUnit>
                  <PitchUnit>
                    <SquadPitchUnit {...sharedPitchElementProps(4)} />
                  </PitchUnit>
                  <PitchUnit>
                    <SquadPitchUnit {...sharedPitchElementProps(5)} />
                  </PitchUnit>
                  <PitchUnit>
                    <SquadPitchUnit {...sharedPitchElementProps(6)} />
                  </PitchUnit>
                  <PitchUnit>
                    <SquadPitchUnit {...sharedPitchElementProps(7)} />
                  </PitchUnit>
                </PitchRow>
                <PitchRow>
                  <PitchUnit>
                    <SquadPitchUnit {...sharedPitchElementProps(8)} />
                  </PitchUnit>
                  <PitchUnit>
                    <SquadPitchUnit {...sharedPitchElementProps(9)} />
                  </PitchUnit>
                  <PitchUnit>
                    <SquadPitchUnit {...sharedPitchElementProps(10)} />
                  </PitchUnit>
                  <PitchUnit>
                    <SquadPitchUnit {...sharedPitchElementProps(11)} />
                  </PitchUnit>
                  <PitchUnit>
                    <SquadPitchUnit {...sharedPitchElementProps(12)} />
                  </PitchUnit>
                </PitchRow>
                <PitchRow>
                  <PitchUnit>
                    <SquadPitchUnit {...sharedPitchElementProps(13)} />
                  </PitchUnit>
                  <PitchUnit>
                    <SquadPitchUnit {...sharedPitchElementProps(14)} />
                  </PitchUnit>
                  <PitchUnit>
                    <SquadPitchUnit {...sharedPitchElementProps(15)} />
                  </PitchUnit>
                </PitchRow>
              </Pitch>
            </Box>
          </TabPanel>
          <TabPanel label={t("createSquad.listView", "List View")} link="pitch">
            <Box mt={4} mb={4}>
              {Object.keys(positionsByElementType).map((et) =>
                listTable(
                  sharedSquadListTableProps(
                    positionsByElementType[et],
                    parseInt(et, 10),
                    et,
                  ),
                ),
              )}
            </Box>
          </TabPanel>
        </Tabs>
      </StyledPitchWrapper>
      {/* <ShirtSoonAlert /> */}
      {renderMenu()}

      {nextEvent && (
        <DialogManager
          render={(showDialog, handleShow, handleHide) => (
            <>
              <SaveBar
                hasUnsavedChanges={canEnter}
                unsavedChangesText={t(
                  "unsavedChanges",
                  "You have unsaved changes",
                )}
              >
                <Button
                  variant="primary"
                  onClick={handleShow}
                  disabled={!canEnter}
                  width={1}
                >
                  {submitButtonText[mode]}
                </Button>
              </SaveBar>
              {showDialog && submitDialog(handleHide, allowNavigation)}
            </>
          )}
        />
      )}
    </div>
  );
};

export default withTranslation()(CreateSquad);
